if (!customElements.get('dcoded-cart-promo')) {
    class DcodedCartPromo extends HTMLElement {
      connectedCallback() {
        this._sectionId = this.dataset.sectionId || '';
        this._portalReady = false;
      
        this._reconcilePortalNodes();
        this._closeSheet();
        this._bindLocalHandlers();
        this._ensurePortalNodes();

        requestAnimationFrame(() => this._ensurePortalNodes());
      
        DcodedCartPromo._ensureGlobalHandlers();

        var drawer = this.closest('cart-drawer-component');
        if (drawer && !drawer.hasAttribute('data-promo-events-bound')) {
          drawer.setAttribute('data-promo-events-bound', 'true');
          drawer.addEventListener('dialog:close', () => {
            DcodedCartPromo._withPromo(drawer, (promo) => promo._closeSheet());
          });
          drawer.addEventListener('dialog:open', () => {
            DcodedCartPromo._withPromo(drawer, (promo) => {
              promo._reconcilePortalNodes();
              promo._ensurePortalNodes();
              promo._closeSheet();
            });
          });
        }

        var confettiWrap = this.querySelector('[data-promo-confetti]');
        if (this.dataset.appliedCode && confettiWrap) {
          this._burstConfetti(confettiWrap);
        }
      }

      disconnectedCallback() {
        this._closeSheet();
        this.removeEventListener('click', this._boundLocalClick, true);

        if (this._sheet && this._sheetClickHandler) {
          this._sheet.removeEventListener('click', this._sheetClickHandler, true);
          this._sheetClickHandler = null;
        }

        var drawerRoot = this._getDrawerRoot();
        if (drawerRoot) drawerRoot.removeAttribute('data-promo-open');

        this._overlay = null;
        this._sheet = null;
        this._portalReady = false;
      }

      _handleLocalClick(e) {
        var target = e.target;
        if (!(target instanceof Element)) return;

        if (target.closest('[data-promo-remove]')) {
          e.preventDefault();
          e.stopPropagation();
          this._removeCode();
          return;
        }

        if (target.closest('[data-promo-change], [data-promo-trigger]')) {
          e.preventDefault();
          e.stopPropagation();
          void this._openSheet();
        }
      }

      _bindLocalHandlers() {
        if (!this._boundLocalClick) {
          this._boundLocalClick = (e) => this._handleLocalClick(e);
        }
        this.removeEventListener('click', this._boundLocalClick, true);
        this.addEventListener('click', this._boundLocalClick, true);
      }

      static _getPromoFromTarget(target) {
        if (!(target instanceof Element)) return null;

        var promo = target.closest('dcoded-cart-promo');
        if (promo) return promo;

        var dialog = target.closest('.cart-drawer__dialog');
        if (dialog) return dialog.querySelector('dcoded-cart-promo');

        return document.querySelector('cart-drawer-component dcoded-cart-promo');
      }

      static _runPromoAction(target, action) {
        var promo = DcodedCartPromo._getPromoFromTarget(target);
        if (!promo) return;

        var run = function () {
          DcodedCartPromo._initInstance(promo);
          action(promo);
        };

        if (typeof promo._openSheet === 'function') {
          run();
          return;
        }

        customElements.whenDefined('dcoded-cart-promo').then(run);
      }

      static _initInstance(promo) {
        if (!(promo instanceof HTMLElement)) return;

        if (typeof promo._bindLocalHandlers === 'function') {
          promo._bindLocalHandlers();
        }
        if (typeof promo._reconcilePortalNodes === 'function') {
          promo._reconcilePortalNodes();
        }
        if (typeof promo._ensurePortalNodes === 'function') {
          promo._ensurePortalNodes();
        }
      }

      _resolvePortalNodes() {
        var drawerRoot = this._getDrawerRoot();
        var nextOverlay = this.querySelector('[data-promo-overlay]');
        var nextSheet = this.querySelector('[data-promo-sheet]');

        if (!nextOverlay && this._overlay?.isConnected) {
          nextOverlay = this._overlay;
        }
        if (!nextSheet && this._sheet?.isConnected) {
          nextSheet = this._sheet;
        }

        if (drawerRoot) {
          if (!nextOverlay) {
            nextOverlay = drawerRoot.querySelector('[data-promo-overlay]');
          }
          if (!nextSheet) {
            nextSheet = drawerRoot.querySelector('[data-promo-sheet]');
          }
        }

        return { nextOverlay, nextSheet };
      }

      _reconcilePortalNodes() {
        var drawerRoot = this._getDrawerRoot();
        var resolved = this._resolvePortalNodes();
        var nextOverlay = resolved.nextOverlay;
        var nextSheet = resolved.nextSheet;

        if (drawerRoot) {
          drawerRoot.querySelectorAll('[data-promo-overlay], [data-promo-sheet]').forEach((node) => {
            if (node !== nextOverlay && node !== nextSheet) {
              node.remove();
            }
          });
        }

        [this._overlay, this._sheet].forEach((node) => {
          if (!node || node === nextOverlay || node === nextSheet) return;
          if (drawerRoot && node.parentElement === drawerRoot) {
            node.remove();
          }
        });

        this._overlay = nextOverlay ?? null;
        this._sheet = nextSheet ?? null;
        this._portalReady = false;
      }

      _cacheNodes() {
        this._reconcilePortalNodes();
      }

      _reconcileAfterCartMorph() {
        var wasOpen = Boolean(this._sheet?.classList.contains('dcoded-promo__sheet--open'));

        var drawerRoot = this._getDrawerRoot();
        if (drawerRoot) drawerRoot.removeAttribute('data-promo-open');

        this._reconcilePortalNodes();
        this._ensurePortalNodes();
        this._bindLocalHandlers();

        if (wasOpen) {
          this._openSheet();
        } else {
          this._closeSheet();
        }
      }

      static _ensureGlobalHandlers() {
        if (DcodedCartPromo._globalHandlersBound) return;
        DcodedCartPromo._globalHandlersBound = true;

        document.addEventListener('click', (e) => {
          var target = e.target;
          if (!(target instanceof Element)) return;
          if (!target.closest('cart-drawer-component, .cart-drawer__dialog')) return;

          var isTrigger = target.closest('[data-promo-trigger], [data-promo-change]');
          var isRemove = target.closest('[data-promo-remove]');
          if (!isTrigger && !isRemove) return;

          e.preventDefault();
          e.stopPropagation();

          if (isRemove) {
            DcodedCartPromo._runPromoAction(target, (promo) => promo._removeCode());
            return;
          }

          DcodedCartPromo._runPromoAction(target, (promo) => {
            void promo._openSheet();
          });
        }, true);

        document.addEventListener('click', (e) => {
          var target = e.target;
          if (!(target instanceof Element)) return;

          var dialog = target.closest('.cart-drawer__dialog');
          if (!dialog) return;

          var isSheetInteraction = Boolean(
            target.closest('[data-promo-close]') ||
            target.closest('[data-promo-overlay].dcoded-promo__overlay--visible') ||
            target.closest('[data-tap-code]')
          );

          if (!isSheetInteraction) return;

          var promo = dialog.querySelector('dcoded-cart-promo');
          if (!promo) return;

          if (typeof promo._openSheet !== 'function') {
            DcodedCartPromo._withPromo(dialog, function(upgradedPromo) {
              if (target.closest('[data-promo-close], [data-promo-overlay].dcoded-promo__overlay--visible')) {
                upgradedPromo._closeSheet();
              } else if (target.closest('[data-tap-code]')) {
                var tapBtn = target.closest('[data-tap-code]');
                if (tapBtn) upgradedPromo._tapToUse(tapBtn);
              }
            });
            return;
          }

          if (target.closest('[data-promo-close]')) {
            e.preventDefault();
            promo._closeSheet();
            return;
          }

          if (target.closest('[data-promo-overlay].dcoded-promo__overlay--visible')) {
            promo._closeSheet();
            return;
          }

          var tapBtn = target.closest('[data-tap-code]');
          if (tapBtn) {
            e.preventDefault();
            promo._tapToUse(tapBtn);
          }
        }, true);

        document.addEventListener('submit', (e) => {
          var form = e.target;
          if (!(form instanceof HTMLFormElement) || !form.matches('[data-promo-form]')) return;

          var dialog = form.closest('.cart-drawer__dialog');
          var promo = dialog?.querySelector('dcoded-cart-promo');
          if (!promo || typeof promo._applyFromInput !== 'function') return;

          e.preventDefault();
          promo._applyFromInput();
        });

        document.addEventListener('cart:update', (event) => {
          if (event.detail?.data?.source === 'dcoded-cart-promo') return;

          requestAnimationFrame(() => {
            document.querySelectorAll('dcoded-cart-promo').forEach((promo) => {
              if (typeof promo._reconcileAfterCartMorph === 'function') {
                promo._reconcileAfterCartMorph();
              }
            });
            DcodedCartPromo._scheduleDiscountRevalidate();
          });
        });
      }

      static _scheduleDiscountRevalidate() {
        clearTimeout(DcodedCartPromo._revalidateTimer);
        DcodedCartPromo._revalidateTimer = setTimeout(function() {
          void DcodedCartPromo.revalidateCartDiscounts();
        }, 150);
      }

      _getDrawerRoot() {
        return (
          this.closest('.cart-drawer__dialog') ||
          document.querySelector('cart-drawer-component .cart-drawer__dialog')
        );
      }

      _isSheetOpen() {
        return Boolean(this._sheet?.classList.contains('dcoded-promo__sheet--open'));
      }

      static _closeAllPromoSheets() {
        document.querySelectorAll('dcoded-cart-promo').forEach((promo) => {
          if (typeof promo._closeSheet === 'function') {
            promo._closeSheet();
          }
        });
      }

      async _updateSubscriptionState() {
        var cart = null;
        try {
          var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (res.ok) cart = await res.json();
        } catch {
          cart = null;
        }

        var hasSubscription = cart ? await this._cartHasSubscription(cart) : this.dataset.hasSubscription === 'true';
        var hasOneTime = cart ? this._cartHasOneTime(cart) : true;

        this.dataset.hasSubscription = hasSubscription ? 'true' : 'false';
        this.dataset.hasOneTime = hasOneTime ? 'true' : 'false';

        var form = this._sheet?.querySelector('[data-promo-form]');
        var input = this._sheet?.querySelector('.dcoded-promo__input');
        var tapButtons = this._sheet?.querySelectorAll('[data-tap-code]');

        this._hideSubscriptionNotice();
        if (form) form.removeAttribute('data-promo-disabled');
        if (input) input.readOnly = false;
        tapButtons?.forEach(function(btn) {
          btn.removeAttribute('disabled');
          btn.classList.remove('dcoded-promo__tap-btn--subscription-locked');
        });

        this._syncTapButtonStates();
        return hasSubscription;
      }

      _markTapApplied(btn) {
        btn.textContent = 'APPLIED';
        btn.classList.add('dcoded-promo__tap-btn--applied');
        btn.classList.remove('dcoded-promo__tap-btn--pulse', 'dcoded-promo__tap-btn--loading');
        btn.setAttribute('aria-disabled', 'true');
        btn.removeAttribute('disabled');
      }

      _resetTapButton(btn) {
        btn.textContent = 'TAP TO USE';
        btn.classList.remove(
          'dcoded-promo__tap-btn--applied',
          'dcoded-promo__tap-btn--pulse',
          'dcoded-promo__tap-btn--loading'
        );
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('disabled');
      }

      _syncTapButtonStates() {
        this._cacheNodes();
        var existingCodes = this._getExistingCodes().map(function(code) {
          return code.toLowerCase();
        });

        this._sheet?.querySelectorAll('[data-tap-code]').forEach((btn) => {
          var code = (btn.dataset.tapCode || '').toLowerCase();
          if (existingCodes.includes(code)) {
            this._markTapApplied(btn);
          } else if (!btn.classList.contains('dcoded-promo__tap-btn--loading')) {
            this._resetTapButton(btn);
          }
        });
      }

      _ensurePortalNodes() {
        this._cacheNodes();
        if (!this._overlay || !this._sheet) return;

        var drawerRoot = this._getDrawerRoot();
        if (!drawerRoot) return;

        if (this._overlay.parentElement !== drawerRoot) {
          drawerRoot.appendChild(this._overlay);
        }
        if (this._sheet.parentElement !== drawerRoot) {
          drawerRoot.appendChild(this._sheet);
        }

        this._overlay.style.setProperty('display', 'block', 'important');
        this._sheet.style.setProperty('display', 'flex', 'important');
        this._portalReady = true;
        this._bindSheetHandlers();
      }

      _bindSheetHandlers() {
        if (!this._sheet) return;

        if (this._sheetClickHandler) {
          this._sheet.removeEventListener('click', this._sheetClickHandler, true);
        }

        this._sheetClickHandler = (e) => {
          var target = e.target;
          if (!(target instanceof Element)) return;

          var tapBtn = target.closest('[data-tap-code]');
          if (tapBtn) {
            e.preventDefault();
            e.stopPropagation();
            this._tapToUse(tapBtn);
            return;
          }

          if (target.closest('[data-promo-close]')) {
            e.preventDefault();
            e.stopPropagation();
            this._closeSheet();
          }
        };

        this._sheet.addEventListener('click', this._sheetClickHandler, true);
      }
      _getInlineErrorEl() {
        return this.querySelector('[data-promo-inline-error]');
      }

      _showInlineError(message) {
        var el = this._getInlineErrorEl();
        if (el) el.textContent = message;
      }

      _clearInlineError() {
        var el = this._getInlineErrorEl();
        if (el) el.textContent = '';
      }

      _cartHasSubscriptionFromCart(cart) {
        if (!cart?.items) return this.dataset.hasSubscription === 'true';

        return cart.items.some(function(item) {
          return Boolean(
            item.selling_plan_allocation ||
            item.selling_plan_id ||
            item.selling_plan
          );
        });
      }

      async _cartHasSubscription(cartHint) {
        try {
          var cart = cartHint;
          if (!cart?.items) {
            var res = await fetch('/cart.js', {
              headers: { Accept: 'application/json' },
            });
            if (!res.ok) return this.dataset.hasSubscription === 'true';
            cart = await res.json();
          }

          return this._cartHasSubscriptionFromCart(cart);
        } catch {
          return this.dataset.hasSubscription === 'true';
        }
      }

      _cartHasOneTime(cart) {
        return (cart?.items || []).some(function(item) {
          return !item.selling_plan_allocation && !item.selling_plan_id && !item.selling_plan;
        });
      }

      _getPromoErrorMessage(code, data) {
        var hasSubscription = this._cartHasSubscriptionFromCart(data);
        var hasOneTime = this._cartHasOneTime(data);
        var entry = (data.discount_codes || []).find(function(d) {
          return (d.code || '').toLowerCase() === code.toLowerCase();
        });

        if (!entry || entry.applicable !== false) {
          return 'This code isn\u2019t valid for your cart.';
        }

        if (hasSubscription && hasOneTime) {
          return 'This code does not apply to Subscribe & Save items. Switch those lines to one-time purchase or use a compatible code.';
        }

        if (hasSubscription) {
          return 'This code is for one-time purchases only. Switch Subscribe & Save items to one-time or remove them.';
        }

        return 'This code is not valid for one-time items in your cart. It may require a subscription.';
      }

      _showSubscriptionNotice(message) {
        var notice = this._sheet?.querySelector('[data-promo-subscription-notice]');
        if (notice) {
          notice.textContent = message;
          notice.hidden = false;
        }
      }

      _hideSubscriptionNotice() {
        var notice = this._sheet?.querySelector('[data-promo-subscription-notice]');
        if (notice) notice.hidden = true;
      }

      static _extractCodesFromCart(cart) {
        var codes = new Set();

        for (var i = 0; i < (cart.cart_level_discount_applications || []).length; i += 1) {
          var app = cart.cart_level_discount_applications[i];
          if (app.type === 'discount_code' && app.title) codes.add(app.title);
        }

        for (var j = 0; j < (cart.items || []).length; j += 1) {
          var item = cart.items[j];
          for (var k = 0; k < (item.line_level_discount_allocations || []).length; k += 1) {
            var alloc = item.line_level_discount_allocations[k];
            var discountApp = alloc.discount_application;
            if (discountApp?.type === 'discount_code' && discountApp.title) {
              codes.add(discountApp.title);
            }
          }
        }

        return [...codes];
      }

      static _getSectionIdsStatic() {
        var ids = [];
        document.querySelectorAll('cart-items-component[data-section-id]').forEach(function(component) {
          if (component instanceof HTMLElement && component.dataset.sectionId) {
            ids.push(component.dataset.sectionId);
          }
        });
        return ids;
      }

      static async revalidateCartDiscounts() {
        try {
          var cartRes = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!cartRes.ok) return;

          var cart = await cartRes.json();
          var codes = DcodedCartPromo._extractCodesFromCart(cart);
          if (!codes.length) return;

          var sectionIds = DcodedCartPromo._getSectionIdsStatic();
          if (!sectionIds.length) return;

          var url =
            typeof Theme !== 'undefined' && Theme.routes?.cart_update_url
              ? Theme.routes.cart_update_url
              : '/cart/update.js';

          var res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount: codes.join(','),
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          if (!res.ok) return;

          var data = await res.json();
          var discountCodes = data.discount_codes || [];
          var hasInapplicable = discountCodes.some(function(d) {
            return d.applicable === false;
          });

          if (!hasInapplicable) return;

          var applicableCodes = discountCodes
            .filter(function(d) {
              return d.applicable !== false;
            })
            .map(function(d) {
              return d.code;
            });

          var fixRes = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount: applicableCodes.join(','),
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          if (!fixRes.ok) return;

          var fixData = await fixRes.json();
          DcodedCartPromo._refreshCartSections(fixData);
          window.__dcodedCartPromoReconcile?.();
        } catch (error) {
          console.error('Dcoded promo revalidate:', error);
        }
      }

      async _openSheet() {
        DcodedCartPromo._initInstance(this);
        this._reconcilePortalNodes();

        if (this._isSheetOpen()) return;

        DcodedCartPromo._closeAllPromoSheets();
        this._clearInlineError();
        this._ensurePortalNodes();

        if (!this._sheet || !this._overlay) {
          await new Promise(function (resolve) {
            requestAnimationFrame(function () {
              requestAnimationFrame(resolve);
            });
          });
          this._reconcilePortalNodes();
          this._ensurePortalNodes();
        }

        if (!this._sheet || !this._overlay) return;

        await this._updateSubscriptionState();

        this._reconcilePortalNodes();
        this._ensurePortalNodes();

        if (!this._sheet || !this._overlay || !document.body.contains(this._sheet)) return;

        var drawerRoot = this._getDrawerRoot();
        var input = this._sheet.querySelector('.dcoded-promo__input');
        var error = this._sheet.querySelector('[data-promo-error]');

        if (drawerRoot) drawerRoot.setAttribute('data-promo-open', 'true');

        this._overlay.style.setProperty('display', 'block', 'important');
        this._sheet.style.setProperty('display', 'flex', 'important');

        this._sheet.classList.add('dcoded-promo__sheet--open');
        this._overlay.classList.add('dcoded-promo__overlay--visible');

        this._sheet.setAttribute('aria-hidden', 'false');
        this._overlay.setAttribute('aria-hidden', 'false');

        if (input) {
          input.value = '';
          input.readOnly = false;
          setTimeout(function() {
            input.focus();
          }, 250);
        }

        if (error) error.textContent = '';
        this._syncTapButtonStates();
      }
      _closeSheet() {
        this._reconcilePortalNodes();

        if (!this._sheet || !this._overlay) return;

        var drawerRoot = this._getDrawerRoot();
        if (drawerRoot) drawerRoot.removeAttribute('data-promo-open');

        this._sheet.classList.remove('dcoded-promo__sheet--open');
        this._overlay.classList.remove('dcoded-promo__overlay--visible');
        this._sheet.setAttribute('aria-hidden', 'true');
        this._overlay.setAttribute('aria-hidden', 'true');
        this._hideSubscriptionNotice();
      }

      _tapToUse(btn) {
        if (btn.classList.contains('dcoded-promo__tap-btn--applied')) return;
        if (btn.classList.contains('dcoded-promo__tap-btn--loading')) return;

        const code = btn.dataset.tapCode || '';
        if (!code) return;

        btn.classList.add('dcoded-promo__tap-btn--pulse', 'dcoded-promo__tap-btn--loading');
        btn.textContent = 'APPLYING...';
        void this._applyCode(code, btn);
      }

      _applyFromInput() {
        this._cacheNodes();
        var input = this._sheet?.querySelector('.dcoded-promo__input');
        var error = this._sheet?.querySelector('[data-promo-error]');
        if (!input) return;
        const code = input.value.trim();
        if (!code) return;
        if (error) error.textContent = '';
        this._hideSubscriptionNotice();
        void this._applyCode(code);
      }

      _getCartUpdateUrl() {
        if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url) {
          return Theme.routes.cart_update_url;
        }
        return '/cart/update.js';
      }

      _getSectionIds() {
        const ids = [];
        document.querySelectorAll('cart-items-component').forEach((component) => {
          if (component instanceof HTMLElement && component.dataset.sectionId) {
            ids.push(component.dataset.sectionId);
          }
        });
        if (this._sectionId && !ids.includes(this._sectionId)) {
          ids.push(this._sectionId);
        }
        return ids;
      }

      async _applyCode(code, tappedBtn) {
        this._cacheNodes();
        var error = this._sheet?.querySelector('[data-promo-error]');
        this._hideSubscriptionNotice();

        try {
          const existingCodes = this._getExistingCodes();
          if (existingCodes.map(c => c.toLowerCase()).includes(code.toLowerCase())) {
            if (tappedBtn) this._markTapApplied(tappedBtn);
            if (error) error.textContent = 'This code is already applied.';
            this._resetTapButtons();
            return;
          }

          const allCodes = [...existingCodes, code].join(',');
          const url = this._getCartUpdateUrl();
          const sectionIds = this._getSectionIds();

          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              discount: allCodes,
              sections: sectionIds.join(','),
              sections_url: window.location.pathname
            }),
          });

          const data = await res.json();

          if (data.discount_codes && data.discount_codes.find(
            function(d) { return d.code.toLowerCase() === code.toLowerCase() && d.applicable === false; }
          )) {
            var message = this._getPromoErrorMessage(code, data);
            if (error) error.textContent = '';
            this._showSubscriptionNotice(message);
            this._resetTapButtons();
            return;
          }

          if (tappedBtn) {
            this._markTapApplied(tappedBtn);
          } else {
            this._sheet?.querySelectorAll('[data-tap-code]').forEach((btn) => {
              if ((btn.dataset.tapCode || '').toLowerCase() === code.toLowerCase()) {
                this._markTapApplied(btn);
              }
            });
          }

          await new Promise(function(resolve) {
            setTimeout(resolve, 500);
          });

          this._closeSheet();
          this._refreshCart(data);
        } catch (err) {
          if (error) error.textContent = 'Something went wrong. Please try again.';
          this._resetTapButtons();
        }
      }

      async _removeCode() {
        const currentCode = this.dataset.appliedCode;
        if (!currentCode) return;

        const existingCodes = this._getExistingCodes();
        const idx = existingCodes.findIndex(c => c.toLowerCase() === currentCode.toLowerCase());
        if (idx !== -1) existingCodes.splice(idx, 1);

        try {
          const url = this._getCartUpdateUrl();

          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              discount: existingCodes.join(','),
              sections: this._getSectionIds().join(','),
              sections_url: window.location.pathname
            }),
          });

          const data = await res.json();
          DcodedCartPromo._refreshCartSections(data);
        } catch (err) {
          console.error('Failed to remove promo code', err);
          location.reload();
        }
      }

      _getExistingCodes() {
        const pills = document.querySelectorAll('.cart-discount__pill');
        const codes = [];
        pills.forEach((p) => {
          if (p.dataset.discountCode) codes.push(p.dataset.discountCode);
        });
        if (this.dataset.appliedCode && !codes.map(c => c.toLowerCase()).includes(this.dataset.appliedCode.toLowerCase())) {
          codes.push(this.dataset.appliedCode);
        }
        return codes;
      }

      _refreshCart(data) {
        DcodedCartPromo._refreshCartSections(data);
      }

      static _refreshCartSections(data) {
        const sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach((component) => {
          if (component instanceof HTMLElement && component.dataset.sectionId) {
            sectionIds.push(component.dataset.sectionId);
          }
        });

        let updated = false;

        sectionIds.forEach((sectionId) => {
          const sectionHtml = data.sections?.[sectionId];
          if (!sectionHtml) return;

          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, 'text/html');
          const component = document.querySelector(
            'cart-items-component[data-section-id="' + sectionId + '"]'
          );

          if (!component) return;

          const newFooter = doc.querySelector('.cart-drawer__footer');
          const oldFooter = component.querySelector('.cart-drawer__footer');
          if (newFooter && oldFooter) {
            oldFooter.replaceWith(newFooter);
            updated = true;
          }

          const newItems = doc.querySelector('.cart-drawer__items');
          const oldItems = component.querySelector('.cart-drawer__items');
          if (newItems && oldItems) {
            oldItems.replaceWith(newItems.cloneNode(true));
            updated = true;
          }
        });

        requestAnimationFrame(() => {
          if (typeof window.__dcodedCartPromoReconcile === 'function') {
            window.__dcodedCartPromoReconcile();
            return;
          }

          DcodedCartPromo._withPromo(document.querySelector('cart-drawer-component'), (promo) => {
            promo._reconcileAfterCartMorph();
          });
        });

        if (!updated) {
          location.reload();
        }
      }

      _resetTapButtons() {
        this._cacheNodes();
        var existingCodes = this._getExistingCodes().map(function(code) {
          return code.toLowerCase();
        });

        this._sheet?.querySelectorAll('[data-tap-code]').forEach((btn) => {
          var code = (btn.dataset.tapCode || '').toLowerCase();
          btn.classList.remove('dcoded-promo__tap-btn--pulse', 'dcoded-promo__tap-btn--loading');

          if (existingCodes.includes(code)) {
            this._markTapApplied(btn);
          } else {
            this._resetTapButton(btn);
          }
        });
      }

      static _withPromo(root, callback) {
        if (!(root instanceof Element)) return;

        var run = function() {
          var promo = root.querySelector('dcoded-cart-promo');
          if (promo && typeof promo._ensurePortalNodes === 'function') {
            callback(promo);
          }
        };

        if (customElements.get('dcoded-cart-promo')) {
          run();
          return;
        }

        customElements.whenDefined('dcoded-cart-promo').then(run);
      }

      _burstConfetti(wrap) {
        const colors = ['#1A432D', '#ff6fad', '#ffd1e4', '#fbd6e3', '#f7c8dd', '#ff9ec7', '#e8b9f3', '#fce4ec'];
        const shapes = ['circle', 'heart', 'rect'];

        for (let i = 0; i < 35; i++) {
          const piece = document.createElement('span');
          piece.classList.add('confetti-piece');

          const shape = shapes[Math.floor(Math.random() * shapes.length)];
          const color = colors[Math.floor(Math.random() * colors.length)];
          const left = Math.random() * 100;
          const delay = Math.random() * 0.5;
          const rotate = (Math.random() - 0.5) * 360;
          const size = 4 + Math.random() * 5;

          piece.style.left = left + '%';
          piece.style.animationDelay = delay + 's';
          piece.style.setProperty('--confetti-rotate', rotate + 'deg');

          if (shape === 'circle') {
            piece.style.width = size + 'px';
            piece.style.height = size + 'px';
            piece.style.borderRadius = '50%';
            piece.style.background = color;
          } else if (shape === 'heart') {
            piece.innerHTML = '<svg width="' + size + '" height="' + size + '" viewBox="0 0 10 10"><path d="M5 9 C3 7,0 5,0 3 A2 2,0,0,1,5 3 A2 2,0,0,1,10 3 C10 5,7 7,5 9Z" fill="' + color + '"/></svg>';
          } else {
            piece.style.width = (size * 0.6) + 'px';
            piece.style.height = size + 'px';
            piece.style.background = color;
            piece.style.borderRadius = '1px';
          }

          wrap.appendChild(piece);
        }

        setTimeout(() => {
          wrap.querySelectorAll('.confetti-piece').forEach((p) => p.remove());
        }, 2500);
      }
    }

    DcodedCartPromo._ensureGlobalHandlers();
    customElements.define('dcoded-cart-promo', DcodedCartPromo);
    window.DcodedCartPromo = DcodedCartPromo;
} else if (!window.DcodedCartPromo) {
    window.DcodedCartPromo = customElements.get('dcoded-cart-promo');
}

window.__dcodedCartPromoReconcile = function () {
  var Promo = window.DcodedCartPromo;
  if (!Promo) return;

  document.querySelectorAll('dcoded-cart-promo').forEach((promo) => {
    if (customElements.get('dcoded-cart-promo')) {
      customElements.upgrade(promo);
    }

    Promo._initInstance(promo);

    if (typeof promo._reconcileAfterCartMorph === 'function') {
      promo._reconcileAfterCartMorph();
    }
  });
};

var dcodedPromoReconcileTimer;

function observeCartPromoMount() {
  var drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  var bindFooterObserver = function () {
    if (drawer.hasAttribute('data-dcoded-promo-observed')) return;

    var footer = drawer.querySelector('.cart-drawer__footer');
    if (!footer) return;

    drawer.setAttribute('data-dcoded-promo-observed', 'true');

    var observer = new MutationObserver(function () {
      clearTimeout(dcodedPromoReconcileTimer);
      dcodedPromoReconcileTimer = setTimeout(function () {
        window.__dcodedCartPromoReconcile();
      }, 32);
    });

    observer.observe(footer, { childList: true, subtree: true });
  };

  bindFooterObserver();

  if (!drawer.hasAttribute('data-dcoded-promo-observed')) {
    drawer.addEventListener('dialog:open', bindFooterObserver);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartPromoMount);
} else {
  observeCartPromoMount();
}

document.addEventListener('dcoded-cart-drawer-refreshed', function () {
  if (window.DcodedCartPromo) {
    window.DcodedCartPromo._scheduleDiscountRevalidate();
  }
});

window.__dcodedCartPromoRevalidate = function () {
  if (window.DcodedCartPromo) {
    return window.DcodedCartPromo.revalidateCartDiscounts();
  }
};
